import { leanRowToOpportunity } from "@/app/(app)/whale-watch/whale-watch-stream-rows";
import { artKey, type ArbShare, type LegTool, type PlayShare, type RowTool, type Share, type ShareLinks } from "@/convex/shares";
import { matchupOf, rowLines } from "@/components/fantasy-props/model";
import { bookLiquidity } from "@/components/optimizer/utils-v2/displayed-odds";
import type { DisplayPlayerProp } from "@/components/optimizer/utils-v2";
import { betLabel, displayOrder, marketLabel, playTitle } from "@/lib/arbitrage/labels";
import { toBetLinkSet, type BetLinkSet } from "@/lib/bet-links";
import { holdOf } from "@/lib/arbitrage/plan";
import { legKey } from "@/lib/arbitrage/rows";
import { selectedCovers } from "@/lib/low-hold/rows";
import { playerTag } from "@/lib/fantasy-props/player-tag";
import { SNIPER_SHEET_PLATFORM } from "@/lib/ev-sniper/sheet-state";
import { priceDisplay, pricesWhales, type GradingOf, type PricedRow } from "@/lib/fantasy-props/rows";
import { rowIdOf } from "@/lib/fantasy-props/slip";
import { playerMarketLabel } from "@/lib/market-label";
import { withoutWhales } from "@/lib/whales";
import type { TopicPayload } from "@/lib/odds-screen/live/protocol";
import type {
  ArbLeg,
  ArbitrageTopic,
  ArbRow,
  DiscrepanciesTopic,
  DiscrepancyRow,
  EventHeader,
  LowHoldRow,
  LowHoldTier,
  LowHoldTopic,
  OptimizerTopic,
  PlayerIdentity,
  PropsTopic,
  SniperTopic,
  WhaleTier,
  WhaleTopic,
} from "@/lib/odds-screen/live/types";
import { edgeRange, spreadEdgePct } from "@/lib/prop-discrepancies/rows";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { bestBetLinks, formatBookLiquidity, formatMarketTitle, formatSideName, getBookDisplayName, getPlayMedia, getWhaleVolumeDisplay, type MarketOpportunity } from "@/lib/whale-watch/whale-watch-shared";
import type { BookWeights } from "@/lib/weights-config";
import { decimalToAmerican } from "@/utils/calculators/odds-utils";

export const PROPS_TOOLS = ["optimizer", "fantasy", "sniper"] as const satisfies readonly RowTool[];
export type PropsTool = (typeof PROPS_TOOLS)[number];
export type PropsRef = { readonly tool: PropsTool; readonly league: string; readonly book: string; readonly rowId: string; readonly href: string; readonly weights: BookWeights | null };
export type RowRef = { readonly tool: Exclude<RowTool, PropsTool>; readonly league: string; readonly book: string; readonly side?: string; readonly rowId: string; readonly href: string };
export type LegPair = { readonly book: string; readonly side: string; readonly line: number };
export type LegsRef = { readonly tool: LegTool; readonly league: string; readonly rowId: string; readonly legs: readonly LegPair[]; readonly href: string };
export type PlayRef = PropsRef | RowRef | LegsRef;
export type ShareTopic = OptimizerTopic | PropsTopic | SniperTopic | WhaleTopic | DiscrepanciesTopic | ArbitrageTopic | LowHoldTopic;

type LinkTuple = readonly [string, string, string];

export function isPropsTool(tool: RowTool): tool is PropsTool {
  return (PROPS_TOOLS as readonly RowTool[]).includes(tool);
}

function isPropsRef(ref: PropsRef | RowRef): ref is PropsRef {
  return isPropsTool(ref.tool);
}

function linksOf(set: BetLinkSet | null | undefined): ShareLinks {
  return { generic: set?.generic || null, mobile: set?.mobile || null, desktop: set?.desktop || null };
}

function tupleLinks(links: LinkTuple): ShareLinks {
  return linksOf(toBetLinkSet(links[0], links[1], links[2]));
}

function tagOf(...parts: (string | null | undefined)[]): { tag?: string } {
  const kept = parts.filter((part): part is string => Boolean(part));
  return kept.length === 0 ? {} : { tag: kept.join(" · ") };
}

function keysOrNone(keys: (string | null)[]): string[] {
  return keys.every((key): key is string => key !== null) ? keys : [];
}

function faceArt(player: PlayerIdentity | null, event: EventHeader, game: boolean): string[] {
  return keysOrNone(game ? [artKey(event.awayLogoUrl), artKey(event.homeLogoUrl)] : [artKey(player?.imageUrl)]);
}

export function rowShare(tool: PropsTool, row: DisplayPlayerProp, event: EventHeader, book: string, href: string, capturedAt: number): PlayShare | null {
  const { primary_odds: odds, ev_percentage: ev, odds_to_hit: hit, league } = row;
  if (!league || typeof odds !== "number" || ev === null || !(hit > 0 && hit < 100)) return null;
  const lines = rowLines(row, event);
  const limit = formatBookLiquidity(bookLiquidity(row, book));
  const tag = lines.kind === "player" ? playerTag(row) : null;
  const link = row.__comparison_links?.[book] ?? (row.bet_link ? { generic: row.bet_link } : null);
  return {
    kind: "play",
    tool,
    league,
    leagueId: league.toLowerCase(),
    rowId: rowIdOf(row),
    eventId: event.id,
    matchup: lines.matchup,
    startsAt: event.startMs,
    title: lines.title,
    line: lines.detail,
    book: sportsbookIdToDisplay(book),
    bookId: book,
    odds,
    ev,
    hit: Math.round(hit * 100) / 100,
    ...(limit !== null && { limit }),
    links: linksOf(link),
    capturedAt,
    href,
    art: lines.kind === "game" && !row.team ? faceArt(null, event, true) : keysOrNone([artKey(row.player_image_url)]),
    ...(tag !== null && { tag }),
  };
}

export function whaleShare(opp: MarketOpportunity, href: string, capturedAt: number): PlayShare | null {
  const { market } = opp;
  const side = market.sides[0];
  const startsAt = Date.parse(opp.event_start);
  if (side === undefined || side.edge_percent === undefined || !side.best_book || !Number.isFinite(startsAt)) return null;
  const media = getPlayMedia(opp);
  return {
    kind: "play",
    tool: "whale",
    league: opp.league.toUpperCase(),
    leagueId: "all",
    rowId: opp.id,
    eventId: opp.event_id,
    matchup: opp.away_team && opp.home_team ? `${opp.away_team} @ ${opp.home_team}` : opp.event,
    startsAt,
    title: formatSideName(side, market.market),
    line: formatMarketTitle(market.market, market.market_arity),
    book: getBookDisplayName(side.best_book),
    bookId: side.best_book,
    odds: side.best_odds,
    ev: side.edge_percent,
    links: linksOf(bestBetLinks(side)),
    capturedAt,
    href,
    art: media === null ? [] : keysOrNone(media.kind === "matchup" ? [artKey(media.awayLogoUrl), artKey(media.homeLogoUrl)] : [artKey(media.url)]),
    whale: getWhaleVolumeDisplay(market).value,
  };
}

export function discrepancyShare(row: DiscrepancyRow, event: EventHeader, bookId: string, side: string | undefined, href: string, capturedAt: number): PlayShare | null {
  const book = row.books.find((candidate) => candidate.book === bookId);
  if (book === undefined || book.multiplier !== null || (side !== "over" && side !== "under")) return null;
  const odds = side === "over" ? book.overOdds : book.underOdds;
  if (odds === null) return null;
  const [low, high] = edgeRange(row.min, row.max, book.line, side);
  return {
    kind: "play",
    tool: "discrepancies",
    league: row.league.toUpperCase(),
    leagueId: row.league,
    rowId: row.id,
    side,
    eventId: event.id,
    matchup: matchupOf(event),
    startsAt: event.startMs,
    title: row.entity.name,
    line: `${side === "over" ? "Over" : "Under"} ${book.line} · ${playerMarketLabel(row.market)}`,
    book: sportsbookIdToDisplay(book.book),
    bookId: book.book,
    odds: decimalToAmerican(odds),
    ev: Math.round(spreadEdgePct(low, high) * 100) / 100,
    lines: { min: low, max: high },
    links: tupleLinks(side === "over" ? book.overLinks : book.underLinks),
    capturedAt,
    href,
    art: faceArt(row.player, event, false),
    ...tagOf(row.player?.position, row.team),
  };
}

function percent(fraction: number): number {
  return Number((fraction * 100).toFixed(2));
}

function coversRow(row: ArbRow | LowHoldRow, legs: readonly ArbLeg[]): boolean {
  const key = (cover: readonly ArbLeg[]) => cover.map(legKey).sort().join();
  return selectedCovers(row.legs, new Set(legs.map((leg) => leg.book))).some((cover) => key(cover) === key(legs));
}

function metricOf(tool: LegTool, row: ArbRow | LowHoldRow, legs: readonly ArbLeg[]): ArbShare["metric"] | null {
  if (tool === "lowhold") return coversRow(row, legs) ? { kind: "hold", pct: percent(holdOf(legs.map((leg) => leg.odds))) } : null;
  if (!("picks" in row)) return null;
  const indexes = legs.map((leg) => row.legs.findIndex((candidate) => legKey(candidate) === legKey(leg))).sort((a, b) => a - b).join();
  const pick = row.picks.find((candidate) => [...candidate.legs].sort((a, b) => a - b).join() === indexes);
  return pick === undefined ? null : { kind: "roi", pct: percent(pick.roi), ...(pick.check && { check: true as const }) };
}

export function legsShare(tool: LegTool, row: ArbRow | LowHoldRow, event: EventHeader, picked: readonly ArbLeg[], href: string, capturedAt: number): ArbShare | null {
  if (picked.length < 2 || new Set(picked.map((leg) => leg.side)).size !== picked.length) return null;
  const metric = metricOf(tool, row, picked);
  if (metric === null) return null;
  const legs = displayOrder(picked).map((index) => picked[index]);
  const market = marketLabel(row.market, legs);
  return {
    kind: "arb",
    tool,
    league: row.league.toUpperCase(),
    eventId: event.id,
    matchup: matchupOf(event),
    startsAt: event.startMs,
    title: playTitle(row, event, market),
    market,
    legs: legs.map((leg) => ({ label: betLabel(leg, event, row.entity), book: sportsbookIdToDisplay(leg.book), bookId: leg.book, odds: decimalToAmerican(leg.odds), links: tupleLinks(leg.links) })),
    metric,
    capturedAt,
    href,
    art: faceArt(row.player, event, row.marketType === "game"),
    ...(row.marketType === "player" && tagOf(row.player?.position, row.team)),
  };
}

export function shareTopic(ref: PlayRef, tier: WhaleTier | LowHoldTier = "default"): ShareTopic {
  switch (ref.tool) {
    case "optimizer":
      return { kind: "optimizer", league: ref.league, book: ref.book };
    case "fantasy":
      return { kind: "props", league: ref.league, book: ref.book };
    case "whale":
      return { kind: "whale", sport: "all", tier };
    case "lowhold":
      return { kind: "lowhold", league: ref.league, tier };
    default:
      return { kind: ref.tool, league: ref.league };
  }
}

function pricedRow({ tool, book, rowId, weights: asked }: PropsRef, payload: TopicPayload, gradingOf: GradingOf, diamond: boolean): PricedRow | null {
  const weights = pricesWhales(tool === "sniper" ? SNIPER_SHEET_PLATFORM : book, diamond) || asked === null ? asked : withoutWhales(asked);
  if (payload.kind === "props" && tool !== "sniper") {
    const row = payload.snap.rows.get(rowId);
    const event = row && payload.snap.events.get(row.eventId);
    return row && event ? { row, event, book, grading: gradingOf(book), weights } : null;
  }
  if (payload.kind === "sniper" && tool === "sniper") {
    const row = payload.snap.rows.get(rowId);
    const event = row?.book === book ? payload.snap.events.get(row.eventId) : undefined;
    return row && event ? { row, event, book, grading: gradingOf(book), weights } : null;
  }
  return null;
}

function mintProps(ref: PropsRef, payload: TopicPayload, capturedAt: number, gradingOf: GradingOf, diamond: boolean): PlayShare | null {
  const priced = pricedRow(ref, payload, gradingOf, diamond);
  const display = priced && priceDisplay(priced);
  return priced && display ? rowShare(ref.tool, display, priced.event, priced.book, ref.href, capturedAt) : null;
}

function mintLegs(ref: LegsRef, payload: TopicPayload, capturedAt: number): ArbShare | null {
  if (payload.kind !== ref.tool) return null;
  const row = payload.snap.rows.get(ref.rowId);
  const event = row === undefined ? undefined : payload.snap.events.get(row.eventId);
  if (row === undefined || event === undefined) return null;
  const picked = ref.legs.map((pair) => row.legs.find((leg) => legKey(leg) === legKey(pair)));
  return picked.every((leg): leg is ArbLeg => leg !== undefined) ? legsShare(ref.tool, row, event, picked, ref.href, capturedAt) : null;
}

function mintRow({ tool, book, side, rowId, href }: RowRef, payload: TopicPayload, capturedAt: number): PlayShare | null {
  if (payload.kind === "whale" && tool === "whale") {
    const row = payload.snap.rows.get(rowId);
    const price = row?.recommended_side.prices.find((candidate) => candidate.book === book);
    return row === undefined || price === undefined ? null : whaleShare(leanRowToOpportunity(row, price), href, capturedAt);
  }
  if (payload.kind === "discrepancies" && tool === "discrepancies") {
    const row = payload.snap.rows.get(rowId);
    const event = row === undefined ? undefined : payload.snap.events.get(row.eventId);
    return row === undefined || event === undefined ? null : discrepancyShare(row, event, book, side, href, capturedAt);
  }
  return null;
}

export function mintShare(ref: PlayRef, payload: TopicPayload, capturedAt: number, gradingOf: GradingOf, diamond: boolean): Share | null {
  return "legs" in ref ? mintLegs(ref, payload, capturedAt) : isPropsRef(ref) ? mintProps(ref, payload, capturedAt, gradingOf, diamond) : mintRow(ref, payload, capturedAt);
}
